import React, { useMemo, useRef, useState } from 'react';
import { Dialog } from '@fluentui/react/lib/Dialog';
import { DefaultButton } from '@fluentui/react/lib/Button';
import { SearchBox } from '@fluentui/react/lib/SearchBox';
import { Checkbox, ResponsiveMode } from '@fluentui/react';
import { InView } from 'react-intersection-observer';
import { useTranslation } from 'react-i18next';

import { useUserGroups, IGroup } from '../hooks/useUserGroups';
import { GroupImage } from '../SharedCommon/UserAvatarName';
import backIcon from '../img/icon-Back.png';
import OkIcon from '../img/ok.png';
import { COMMON_MODAL_LIST_STYLE, getDialogThemeClassName, getThemedModalMainStyles } from './dialogStyles';
import {
  COMMON_DIALOG_CONTENT_STYLES,
  COMMON_HEADER_BUTTON_STYLES,
  getSearchBoxStyles,
  getGroupButtonStyles
} from './shellStyles';
import { SearchBoxChrome } from './SearchBoxChrome';
import { useIdigestChatTheme } from '../ThemeContext';
import './Dialog.css';

export interface IPromptPickShareTargetProps {
  onConfirm: (selectedGroupIds: number[]) => void | Promise<void>;
  onDismiss: () => void;
}

export const PromptPickShareTarget: React.FunctionComponent<IPromptPickShareTargetProps> = (props) => {
  const { groupList, filteredGroups, txt, loading, searchQuery, setSearchQuery } = useUserGroups();
  const [selectedGroupIds, setSelectedGroupIds] = useState<Set<number>>(new Set());
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);
  const { t } = useTranslation();
  const { theme } = useIdigestChatTheme();

  const searchBoxStyles = useMemo(() => getSearchBoxStyles(theme), [theme]);
  const groupButtonStyles = useMemo(() => getGroupButtonStyles(theme), [theme]);
  const canConfirm = selectedGroupIds.size > 0 && !submitting;

  const handleGroupToggle = (groupId: number, checked: boolean) => {
    if (submittingRef.current) {
      return;
    }
    setSelectedGroupIds((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(groupId);
      } else {
        next.delete(groupId);
      }
      return next;
    });
  };

  const handleConfirm = () => {
    if (submittingRef.current || selectedGroupIds.size === 0) {
      return;
    }
    submittingRef.current = true;
    setSubmitting(true);
    void Promise.resolve(props.onConfirm(Array.from(selectedGroupIds))).finally(() => {
      submittingRef.current = false;
      setSubmitting(false);
    });
  };

  const handleDismiss = () => {
    if (submittingRef.current) {
      return;
    }
    setSelectedGroupIds(new Set());
    setSearchQuery('');
    props.onDismiss();
  };

  return (
    <Dialog
      hidden={false}
      responsiveMode={ResponsiveMode.small}
      onDismiss={handleDismiss}
      dialogContentProps={{
        showCloseButton: false,
        styles: {
          inner: { padding: '0px' },
          header: { height: '0px' },
          content: COMMON_DIALOG_CONTENT_STYLES
        }
      }}
      modalProps={{
        isBlocking: true,
        styles: { main: getThemedModalMainStyles(theme) },
        containerClassName: `${getDialogThemeClassName(theme)} modal-list-fill`
      }}
    >
      <div className='header-section'>
        <div className='button-wrapper left-btn'>
          <DefaultButton className='header-button' styles={COMMON_HEADER_BUTTON_STYLES} onClick={handleDismiss}>
            <img src={backIcon} alt='Back' className='button-icon' />
          </DefaultButton>
          <span className='button-label'>{t('Back')}</span>
        </div>
        <h3 className='header-title' style={{ fontWeight: 450, margin: '0 0 8px' }}>
          {groupList.length > 0
            ? selectedGroupIds.size > 0
              ? t('Chatroom.SelectedShareTargetsCount', { count: selectedGroupIds.size })
              : t('Chatroom.SelectShareTarget')
            : loading
              ? t('loading')
              : txt || t('NoGroup')}
        </h3>
        <div className='button-wrapper right-btn'>
          <DefaultButton
            className='header-button'
            styles={{
              ...COMMON_HEADER_BUTTON_STYLES,
              rootDisabled: {
                opacity: 0.4,
                cursor: 'not-allowed'
              }
            }}
            onClick={handleConfirm}
            disabled={!canConfirm}
          >
            <img src={OkIcon} alt={t('OK')} className='button-icon' />
          </DefaultButton>
          <span className='button-label' style={{ opacity: canConfirm ? 1 : 0.4 }}>
            {t('OK')}
          </span>
        </div>
      </div>
      {groupList.length > 0 && (
        <div className='dialog-searchbox-wrapper searchbox-wrapper group-list-pane-pad'>
          <SearchBoxChrome>
            <SearchBox
              styles={searchBoxStyles}
              value={searchQuery}
              placeholder={t('Search')}
              onEscape={() => setSearchQuery('')}
              onChange={(_, value) => setSearchQuery(value || '')}
              onClear={() => setSearchQuery('')}
            />
          </SearchBoxChrome>
        </div>
      )}
      <div
        className='PickMeeting fellowshipGroup idigest-chat-modal-list grouplist-wrapper group-list-pane-pad'
        style={COMMON_MODAL_LIST_STYLE}
        data-is-scrollable='true'
      >
        {!!groupList &&
          filteredGroups.map((item: IGroup) => {
            const isSelected = selectedGroupIds.has(item.groupId);
            return (
              <InView key={item.groupId} triggerOnce>
                {({ inView, ref }) => (
                  <ul ref={ref} className='ulGroups group-list-item-row'>
                    <div className='group-list-item-inner'>
                      <div className='group-checkbox-container'>
                        <div onClick={(ev) => ev.stopPropagation()} onMouseDown={(ev) => ev.stopPropagation()}>
                          <Checkbox
                            checked={isSelected}
                            onChange={(ev, checked) => {
                              if (ev) {
                                ev.stopPropagation();
                              }
                              if (checked !== undefined) {
                                handleGroupToggle(item.groupId, checked);
                              }
                            }}
                            styles={{
                              checkbox: {
                                width: '17px',
                                height: '17px',
                                backgroundColor: 'var(--idigest-chat-surface, #ffffff)',
                                borderColor: 'var(--idigest-chat-muted, #b0b0b0)',
                                cursor: 'pointer',
                                selectors: {
                                  '&:hover': {
                                    backgroundColor: 'var(--idigest-chat-surface, #ffffff)',
                                    borderColor: 'var(--idigest-chat-muted, #b0b0b0)'
                                  }
                                }
                              },
                              checkmark: {
                                color: 'var(--idigest-chat-fg, #171717) !important',
                                fontWeight: 'bold !important'
                              }
                            }}
                          />
                        </div>
                      </div>
                      <DefaultButton
                        className='orgGroupButton'
                        styles={{
                          ...groupButtonStyles,
                          flexContainer: {
                            ...groupButtonStyles.flexContainer,
                            minWidth: 0
                          },
                          root: {
                            ...groupButtonStyles.root,
                            padding: '10px 16px',
                            width: 'auto',
                            flex: '1 1 0',
                            minWidth: 0,
                            maxWidth: '100%'
                          }
                        }}
                        onClick={() => handleGroupToggle(item.groupId, !isSelected)}
                      >
                        {inView && (
                          <GroupImage
                            item={item}
                            className={item.isOneOnOneGroup ? 'groupSymbol' : 'groupSymbol groupSymbolBig'}
                          />
                        )}
                        <div className='group-list-item-label' title={item.name}>
                          {item.name}
                        </div>
                      </DefaultButton>
                    </div>
                  </ul>
                )}
              </InView>
            );
          })}
      </div>
    </Dialog>
  );
};
